import { Source, Vector as VectorSource } from 'ol/source';
import { Layer, Vector as VectorLayer } from 'ol/layer';
import { Feature } from 'ol';
import { fromLonLat } from 'ol/proj';
import { Geometry, Point } from 'ol/geom';
import { Style, Icon } from 'ol/style';
import { Map as MGMap } from '@meteogroup/map-layers';

import { VoyagePlansModel } from '../models/voyage-plans-model';
import { VesselPlanningModel, VoyagePlan } from '../models/vessel-planning-model';
import { VoyageRoute, VoyageRoutingModel } from '../models/voyage-routing-model';
import MGPlayerCore, { PLAYER_EVENTS } from '@meteogroup/player-core';
import { PlanningLayers } from './planning-layers';
import { Coordinate } from 'ol/coordinate';
import { SvgIcons } from '../../local_modules/voyage-svg-icons/src/voyage-svg-icons';
import { FleetIcons, MapStyles } from "./map-styles";
import { PlansService } from '../services/plans-service';
import { VoyageShoreContextModel } from '../models/voyage-shore-context-model';
import { VesselsTrackingModel } from '../models/vessels-tracking-model';
import { VesselInfo } from '../models/types/vessels-model.types';
import { UiStateModel, UiStateModelParam } from '../models/ui-state-model';
import { VesselTrack } from '../models/types/vessels-tracking-model.types';
import { PAST_TRAIL_ROTATION_FACTOR } from '../utils/helpers';

const _PROJECTION = (<any>window)["appenv"].projection;
const DEFAULT_SHIP_COLOR = "#003D6D";

const voyagePlanningStyle = new Style({
    image: new Icon({
        src: 'data:image/svg+xml,' + encodeURIComponent(SvgIcons.customVoyagePlanningVesselIcon.strings[0]),
        scale: 1,
        anchor: [0.5, 0.5],
        rotateWithView: true,
        color: "#FFFF00"
    })
})
export class VesselInTimeLayers extends EventTarget
{
    constructor(playerCore: MGPlayerCore, includePlanning?: boolean, isVessel?: boolean, planningLayers?: PlanningLayers)
    {
        super();
        this._includePlanning = !!includePlanning;
        this._isVessel = !!isVessel;
        this._planningLayers = planningLayers || null;
        this._player = playerCore;

        if (this._isVessel) {
            this._shipShape = escape(SvgIcons.customVoyagePlanningVesselIcon.strings[0])
        } else {
            this._shipShape = escape(SvgIcons.customVesselIcon.strings[0])
        }

        this._setPlanCurrent = this._setPlanCurrent.bind(this);
        this._setExecutingPlan = this._setExecutingPlan.bind(this);
        this._setPlansForRoutes = this._setPlansForRoutes.bind(this);
        this._plotVesselsInTime = this._plotVesselsInTime.bind(this);
        this._setVessels = this._setVessels.bind(this);
        this.updateSingleVesselState = this.updateSingleVesselState.bind(this);
    }

    get layers()
    {
        return this._layers;
    }

    async initialize()
    {
        this._vectorSources.push(new VectorSource({}));
        this._layers.push(new VectorLayer({source: this._vectorSources[0], visible: true, zIndex: 5}));    

        if (this._includePlanning)
        {
            for (let i = 0; i < VoyageRoutingModel!.uiRoutes.length; i++)
            {
                const source = new VectorSource({});
                this._vectorSources.push(source);
                this._layers.push(new VectorLayer({source: source, visible: true, zIndex: 5}));

                this._vesselScreenLocations.push([-1, -1]);
            }    
        }

        if (!this._isVessel)
        {
            VoyagePlansModel!.addEventListener('plans-updated', <EventListener>this._setPlanCurrent);
        }
        else
        {
            VesselPlanningModel!.addEventListener('executing-plan-changed', this._setExecutingPlan);
        }

        if (this._includePlanning)
        {
            VoyageRoutingModel!.addEventListener('routes-changed', this._setPlansForRoutes);
        }

        // Vessel 'in-the-future' position
        this._player.addEventListener(PLAYER_EVENTS.TIME_UPDATE, this._plotVesselsInTime);
        UiStateModel!.addEventListener(`${UiStateModelParam.IMO}-change`, this.updateSingleVesselState);
    }

    assignMap(map: MGMap)
    {
        this._map = map;
    }

    clear()
    {
        try
        {
            for (let i = 0; i < this._routes.length; i++)
            {
                this._setRoute(i, null);
            }
        }
        catch (error)
        {
            console.warn(error);
        }
    }

    private _includePlanning: boolean;
    private _isVessel: boolean;
    private _planningLayers: PlanningLayers|null = null;
    private _layers: Layer<Source>[] = [];
    private _map: MGMap|null = null;
    private _player: MGPlayerCore;
    private _vectorSources: VectorSource<Geometry>[] = [];
    private _routes: (VoyageRoute|null)[] = [ null ];
    private _vesselScreenLocations: Coordinate[] = [ [-1, -1] ];
    private _selectedPlan: number = -1;
    private _shipShape: string;
    private filteredVessels: VesselInfo[] = [];
    private simulateSingleVessel: boolean = false;

    private _setPlanCurrent(evt: CustomEvent)
    {
        const plan = VoyagePlansModel!.choosePlan(evt.detail.imoNo);
        if (!plan || plan.routes.length == 0)
        {
            this._setRoute(0, null);
        }
        else
        {
            this._setRoute(0, plan.routes[plan.selectedRouteIndex]);
        }
    }

    private async _setExecutingPlan()
    {
        const plan = await VesselPlanningModel!.getExecutingPlan();

        if (!plan || plan.routes.length === 0 || plan.selectedRouteIndex < 0)
        {
            this._setRoute(0, null);
            return;
        }
        this._setRoute(0, plan.routes[plan.selectedRouteIndex]);
    }

    private _setPlansForRoutes()
    {
        this._selectedPlan = -1;
        const uiRoutes = VoyageRoutingModel!.uiRoutes;
        for (let i = 0; i < uiRoutes.length; i++)
        {
            this._setRoute(i+1, uiRoutes[i].route);
            if (uiRoutes[i].selected)
            {
                this._selectedPlan = i+1;
            }
        }
    }

    private _setRoute(index: number, plan: VoyageRoute|null)
    {
        this.resetVectorSource();
        if(!plan){
            return;
        }
        this._routes = [null];
        this._routes[index] = plan;
        this.filteredVessels = [];
        
        if (this._vectorSources[index]){
            this._vectorSources[index].clear();
        }
        this.deployVectorLayers(this._routes?.length);
        VoyageShoreContextModel?.toggleVesselSelection();
    }

    plotInitialPosition(playerTime: number){
        for (let i = 0; i < this._routes.length; i++)
            {
                const plan = this._routes[i];
                if (!plan || (this._isVessel && !(<VoyageRoute>plan).route_info) || (localStorage.getItem("timeLineView") !== "true"))
                {
                    continue;
                }
    
                const routeStartTime = new Date(plan.route_geometry[0].properties.timestamp).getTime();
                const routeEndTime = new Date(plan.route_geometry[plan.route_geometry.length-1].properties.timestamp).getTime();
    
                if ((<VoyageRoute>plan).route_info && (playerTime < routeStartTime || playerTime > routeEndTime) && !this.simulateSingleVessel) {
                    continue;
                }
    
                const positionAndHeading = VoyageRoutingModel!.getPositionAndHeadingInTime(playerTime, plan);
            
                if (!positionAndHeading) continue;
    
                const screenLocation = this._map!.getPixelFromCoordinate(fromLonLat(positionAndHeading.position, _PROJECTION));
                this._vesselScreenLocations[i] = screenLocation;
    
                let shipColor = "#FFFF00";
                if (this._planningLayers && i > 0 && i-1 < this._planningLayers.colors.uiRouteColors.length)
                {
                    shipColor = "#FFFF00";
                }
    
                const rotate = this._isVessel ? 2 : 180;
                
                let shipStyle: Style;
                if (this._isVessel) {
                    shipStyle = voyagePlanningStyle;
                } else {
                    shipStyle = MapStyles.maerskVesselShapeStyle;
                }
    
                let vesselStyle = shipStyle.clone();
                vesselStyle.getImage().setRotation(positionAndHeading.heading - Math.PI / rotate);
    
                // Use the projected line to get the exact map position (using fromLonLat() gives a slight error for long lines)
                const shipPoint = new Point(positionAndHeading.projectedPosition);
                const shipFeature = new Feature(shipPoint);
                shipFeature.setStyle(vesselStyle);
                if(this._vectorSources?.length <= i){
                    let vectorSource =  new VectorSource({});
                    this._vectorSources.push(vectorSource);
                    this._map?.addLayer(new VectorLayer({source: vectorSource, visible: true, zIndex: 5}));
                }
                this._vectorSources[i]?.clear();
                this._vectorSources[i]?.addFeature(shipFeature);
            }
    }

    _plotVesselsInTime()
    {
        const playerTime = this._player.currentTime;
        const isFilterApplied = UiStateModel?.getValue(UiStateModelParam.Search) ? true : false;

        if (playerTime <= Date.now() && !this.simulateSingleVessel && isFilterApplied)
        {
            // Clear vessel-in-time layer when at current time (or older)
            for (const vectorSource of this._vectorSources)
            {
                vectorSource.clear();
            }
            this.dispatchEvent(new CustomEvent('vessel-in-time-position', { detail: null }));
            this.dispatchEvent(new CustomEvent('past-simulation-limit-reached', {detail: {
                disableBackward: true
            }}));
            this.plotInitialPosition(playerTime);
            return;
        }

        for (let i = 0; i < this._routes.length; i++)
        {
            const plan = this._routes[i];
            if (!plan || (this._isVessel && !(<VoyageRoute>plan).route_info) || (localStorage.getItem("timeLineView") !== "true"))
            {
                continue; // no plan or plan not calculated
            }

            // timestamp of first and last waypoint of route
            const routeStartTime = new Date(plan.route_geometry[0].properties.timestamp).getTime();
            const routeEndTime = new Date(plan.route_geometry[plan.route_geometry.length-1].properties.timestamp).getTime();

            if ((<VoyageRoute>plan).route_info && (playerTime < routeStartTime || playerTime > routeEndTime) && !this.simulateSingleVessel) {
                continue; // currentTime is outside of route start and end time
            }

            const positionAndHeading = VoyageRoutingModel!.getPositionAndHeadingInTime(playerTime, plan);
            if(playerTime <= Date.now()){
                this.plotVesselInPastTrail(playerTime, i);
                continue;
            }
            if (!positionAndHeading) continue;

            const screenLocation = this._map!.getPixelFromCoordinate(fromLonLat(positionAndHeading.position, _PROJECTION));

            // if (Math.abs(screenLocation[0] - this._vesselScreenLocations[i][0]) < 1
            //     && Math.abs(screenLocation[1] - this._vesselScreenLocations[i][1]) < 1)
            // {
            //     continue;
            // }

            // if (i === this._selectedPlan || i === 0)
            // {
            //     this.dispatchEvent(new CustomEvent('vessel-in-time-position', { detail: positionAndHeading.position }));
            // }

            this._vesselScreenLocations[i] = screenLocation;

            let shipColor = "#FFFF00";
            if (this._planningLayers && i > 0 && i-1 < this._planningLayers.colors.uiRouteColors.length)
            {
                shipColor = "#FFFF00";
            }

            const rotate = this._isVessel ? 2 : 180;
            
            let shipStyle: Style;
            if (this._isVessel) {
                shipStyle = voyagePlanningStyle;
            } else {
                shipStyle = MapStyles.maerskVesselShapeStyle;
            }

            let vesselStyle = shipStyle.clone();
            vesselStyle.getImage().setRotation(positionAndHeading.heading - Math.PI / rotate);

            // Use the projected line to get the exact map position (using fromLonLat() gives a slight error for long lines)
            const shipPoint = new Point(positionAndHeading.projectedPosition);
            const shipFeature = new Feature(shipPoint);
            shipFeature.setStyle(vesselStyle);
            if(this._vectorSources?.length <= i){
                let vectorSource =  new VectorSource({});
                this._vectorSources.push(vectorSource);
                this._map?.addLayer(new VectorLayer({source: vectorSource, visible: true, zIndex: 5}));
            }
            this._vectorSources[i]?.clear();
            this._vectorSources[i]?.addFeature(shipFeature);
        }
    }

    deployVectorLayers(n: number){
        for (let i=0; i<n; i++){
            this._vectorSources.push(new VectorSource({}));
            this._map?.addLayer(new VectorLayer({source: this._vectorSources[i], visible: true, zIndex: 5}));
        }    
    }

    async _setVessels(plotInitialPosition = true): Promise<void> {
        let count = VoyageShoreContextModel!.vessels?.length;
        let vessels = VoyageShoreContextModel!.vessels;
        if(count < 1){
            return;
        }
        if(this.simulateSingleVessel || !UiStateModel?.getValue(UiStateModelParam.Search)){
            return;
        }
        if(count > 20){
            vessels = vessels.slice(0, 20)
        }
        if(this.filteredVessels.sort().toString() === vessels.sort().toString()){
            return;
        }
        this.filteredVessels = [...vessels];
        this.resetVectorSource();
        let temp :any= []
        this.dispatchEvent(new CustomEvent('toggle-loader',{detail: {show: true, label: "Loading vessels"}}));
        this.dispatchEvent(new CustomEvent('toggle-player-controls',{detail: {disable: true}}));
        VoyageShoreContextModel?.toggleVesselSelection();
        try{
            let a = await Promise.all(vessels.map(vessel => PlansService.fetchPlans(vessel.imoNo, new Date()))).then((plans) => {
                temp = plans 
            });
        }finally{
            this.dispatchEvent(new CustomEvent('toggle-loader',{detail: {show: false}}));
            this.dispatchEvent(new CustomEvent('toggle-player-controls',{detail: {disable: false}}));
        }
        let values = temp.map((map: any) => map.has("VoyagePlanning") ? map.get("VoyagePlanning") : map.get("ExcelPlan"));
        values = values.filter((item: any) => !!item);
        this._routes = [null];
        for (let i=0;i<values.length;i++){
            this._routes[i] = values[i]?.routes[0];     
        }
        this.deployVectorLayers(this._routes?.length);
        if(plotInitialPosition){
            this.plotInitialPosition(this._player.currentTime);
        }
    }

    resetVectorSource(){
        for(let [index, element] of this._vectorSources?.entries()){
            if (this._vectorSources[index]){
                this._vectorSources[index].clear();
            }
            this._map?.removeLayer(new VectorLayer({source: this._vectorSources[index], visible: true, zIndex: 5}));
        }
        this._vectorSources = [];
    }

    updateSingleVesselState(){
        this.simulateSingleVessel = UiStateModel?.getValue(UiStateModelParam.IMO) ? true : false;
        if(!this.simulateSingleVessel){
            this._setVessels();
        }
    }

    plotVesselInPastTrail(playerTime: number, vectorIndex: number){
        let tracking: VesselTrack | null  = VesselsTrackingModel?.getTracking(Number(UiStateModel?.getValue(UiStateModelParam.IMO))) || null;
        if(!tracking){
            return false;
        }
        
        let positions = tracking.positions;
        let currentPosition: any;

        if(new Date(playerTime) < new Date(positions[positions.length - 1].timestamp)){
            this.dispatchEvent(new CustomEvent('past-simulation-limit-reached', {detail: {
                disableBackward: true
            }}));
            currentPosition = positions[positions.length - 1];
        }else{
            for(let i=0; i<positions.length; i++){
                if(new Date(positions[i].timestamp).getTime() < playerTime){
                    currentPosition = positions[i];
                    break;
                }
            }
        }

        if(currentPosition && currentPosition.latitude && currentPosition.longitude && currentPosition.heading){
            const screenLocation = this._map!.getPixelFromCoordinate(fromLonLat([currentPosition.longitude, currentPosition.latitude], _PROJECTION));
            this._vesselScreenLocations[vectorIndex] = screenLocation;
        
            let shipColor = "#FFFF00";
            if (this._planningLayers && vectorIndex > 0 && vectorIndex-1 < this._planningLayers.colors.uiRouteColors.length)
            {
                shipColor = "#FFFF00";
            }

            const rotate = this._isVessel ? 2 : 180;
            
            let shipStyle: Style;
            if (this._isVessel) {
                shipStyle = voyagePlanningStyle;
            } else {
                shipStyle = MapStyles.maerskVesselShapeStyle;
            }

            let vesselStyle = shipStyle.clone();
            vesselStyle.getImage().setRotation(currentPosition.heading * PAST_TRAIL_ROTATION_FACTOR);
        
            // Use the projected line to get the exact map position (using fromLonLat() gives a slight error for long lines)
            const shipPoint = new Point([currentPosition.longitude,currentPosition.latitude]);
            const shipFeature = new Feature(shipPoint);
            shipFeature.setStyle(vesselStyle);
            shipFeature.getGeometry()?.transform('EPSG:4326', _PROJECTION);
            if(this._vectorSources?.length <= vectorIndex){
                let vectorSource =  new VectorSource({});
                this._vectorSources.push(vectorSource);
                this._map?.addLayer(new VectorLayer({source: vectorSource, visible: true, zIndex: 5}));
            }
            this._vectorSources[vectorIndex]?.clear();
            this._vectorSources[vectorIndex]?.addFeature(shipFeature);
        }
    }

    _resetVessels(){
        if(!this.simulateSingleVessel){
            this.resetVectorSource();
            this._routes = [null];
            this.filteredVessels = [];
        }
    }

    clearPositions(){
        for (const vectorSource of this._vectorSources)
            {
                vectorSource.clear();
            }
    }
};
